class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  // Detect if a query looks like a SKU (mix of letters and numbers, no spaces)
  looksLikeSku(term) {
    return /^[a-zA-Z]{1,6}\d{2,}[a-zA-Z]?$/.test(term) || /^\d{2,}[a-zA-Z]+/.test(term);
  }

  // Generate fuzzy variants for SKU-like queries
  generateSkuVariants(term) {
    const variants = new Set();
    const t = term.toUpperCase();

    // Original term
    variants.add(t);

    // Try inserting a single letter at each position in the alpha prefix
    const match = t.match(/^([A-Z]+)(\d+[A-Z]?)$/);
    if (match) {
      const prefix = match[1];
      const suffix = match[2];
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

      // Insert a letter at each position within the prefix
      for (let i = 0; i <= prefix.length; i++) {
        for (const c of chars) {
          variants.add(prefix.slice(0, i) + c + prefix.slice(i) + suffix);
        }
      }

      // Try removing each letter from the prefix (for longer prefixes)
      if (prefix.length > 1) {
        for (let i = 0; i < prefix.length; i++) {
          variants.add(prefix.slice(0, i) + prefix.slice(i + 1) + suffix);
        }
      }

      // Try swapping adjacent letters in prefix
      for (let i = 0; i < prefix.length - 1; i++) {
        const swapped = prefix.slice(0, i) + prefix[i + 1] + prefix[i] + prefix.slice(i + 2);
        variants.add(swapped + suffix);
      }

      // Also search just the numeric portion
      if (suffix.length >= 3) {
        variants.add(suffix);
      }
    }

    // Remove the original and return up to 5 best variants
    variants.delete(t);
    return Array.from(variants).slice(0, 5);
  }

  fetchPredictiveSearch(term) {
    return fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(term)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        return new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
      });
  }

  // Merge results from multiple searches, deduplicating by product URL
  mergeResults(primaryHtml, secondaryHtmlArray) {
    const parser = new DOMParser();
    const primary = parser.parseFromString('<div>' + primaryHtml + '</div>', 'text/html');
    const primaryList = primary.querySelector('#predictive-search-results-list');
    if (!primaryList) return primaryHtml;

    // Collect existing product URLs
    const existingUrls = new Set();
    primaryList.querySelectorAll('.predictive-search__item--link').forEach(a => {
      existingUrls.add(a.getAttribute('href'));
    });

    // Get the "view all" item to insert before
    const viewAllItem = primaryList.querySelector('.predictive-search__list-item--view-all') ||
                        primaryList.querySelector('#predictive-search-option-search-keywords');

    let added = 0;
    for (const html of secondaryHtmlArray) {
      if (added >= 5) break;
      const doc = parser.parseFromString('<div>' + html + '</div>', 'text/html');
      const items = doc.querySelectorAll('.predictive-search__list-item:not(.predictive-search__list-item--view-all):not(#predictive-search-option-search-keywords)');

      items.forEach(item => {
        if (added >= 5) return;
        const link = item.querySelector('.predictive-search__item--link');
        if (link && !existingUrls.has(link.getAttribute('href'))) {
          existingUrls.add(link.getAttribute('href'));
          if (viewAllItem) {
            primaryList.insertBefore(item, viewAllItem);
          } else {
            primaryList.appendChild(item);
          }
          added++;
        }
      });
    }

    // Update the result count in heading
    const heading = primary.querySelector('.predictive-search__heading');
    if (heading) {
      const total = primaryList.querySelectorAll('.predictive-search__list-item:not(.predictive-search__list-item--view-all):not(#predictive-search-option-search-keywords)').length;
      heading.childNodes.forEach(node => {
        if (node.nodeType === 3 && node.textContent.trim()) {
          node.textContent = node.textContent.replace(/\(\d+\)/, '(' + total + ')');
        }
      });
    }

    return primary.querySelector('#predictive-search-results')
      ? primary.querySelector('#predictive-search-results').parentElement.innerHTML
      : primaryHtml;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const primarySearch = this.fetchPredictiveSearch(searchTerm);

    // If it looks like a SKU, also search fuzzy variants
    if (this.looksLikeSku(searchTerm.trim())) {
      const variants = this.generateSkuVariants(searchTerm.trim());
      const variantSearches = variants.map(v => this.fetchPredictiveSearch(v).catch(() => ''));

      Promise.all([primarySearch, ...variantSearches])
        .then(([primaryHtml, ...secondaryHtmls]) => {
          const validSecondary = secondaryHtmls.filter(h => h && h.includes('predictive-search__item--link'));
          const merged = validSecondary.length > 0
            ? this.mergeResults(primaryHtml, validSecondary)
            : primaryHtml;
          this.cachedResults[queryKey] = merged;
          this.renderSearchResults(merged);
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    } else {
      primarySearch
        .then((resultsMarkup) => {
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          this.close();
          throw error;
        });
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
